/**
 * 7-step aanmeting-wizard.
 *  1 Werknemer-bevestiging  2 Identiteit  3 Schoenmaat  4 Hallux
 *  5 Scan-referentie         6 Pijnplekken  7 Profiel + samenvatting
 *
 * Backend-calls op Voltooien (step 7) in volgorde:
 *   PATCH /uitvoeringen/{id}/adres-controle
 *   PATCH /werknemers/{wid}           (alleen bij wijzigingen step 2)
 *   PATCH /uitvoeringen/{id}/scan     (combineert schoenmaat + scan-ref + hallux in JSON)
 *   POST  /uitvoeringen/{id}/pijnplek × N
 *   PATCH /uitvoeringen/{id}/profiel
 *   PATCH /uitvoeringen/{id}/voltooien
 *
 * V1.5 — PLT-integratie zal werkelijke voet-metrics (lengte/breedte/boog) leveren.
 *   Safe-placeholders (≥50mm, anatomisch plausibel) gebruikt voor pilot 1 happy-path zodat
 *   backend ScanVoetData-validatie (Field ge=50) passeert. Placeholder=true markeert deze
 *   rijen in rapportage zodat ze later geüpdatet kunnen worden zonder echte metingen te
 *   overschrijven.
 */
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiInstance, adminGet, adminPatch, adminPost, formatApiError } from "../../lib/adminApi";
import { useLocalStorageState } from "../../lib/useLocalStorageState";
import { PageHeader, ErrorBanner, formatDate } from "../admin/AdminUI";
import { VoetDiagram } from "./VoetDiagram";
import { NoShowModal, VerzetModal } from "./NoShowVerzetModals";
import { PIJN_LOCATIE_LABELS } from "../../lib/constants";
import { noemNetjes } from "../../lib/namen";

const TOTAL_STEPS = 7;

const identiteitSchema = z.object({
  voorletters: z.string().min(1, "Verplicht"),
  achternaam: z.string().min(1, "Verplicht"),
  geboortedatum: z.string().min(1, "Verplicht"),
  email: z.string().email("Ongeldig e-mail").or(z.literal("")).optional(),
  telefoon: z.string().regex(/^[\d +\-()]{6,20}$/, "Ongeldig telefoon").or(z.literal("")).optional(),
  straat: z.string().min(1, "Verplicht"),
  huisnummer: z.string().min(1, "Verplicht"),
  postcode: z.string().regex(/^\d{4}\s?[A-Za-z]{2}$/, "NL-postcode XXXX AA").or(z.literal("")),
  plaats: z.string().min(1, "Verplicht"),
});

const EMPTY_STATE = {
  step: 1,
  adresGecontroleerd: false,
  identiteit: {},
  identiteitDirty: false,
  maatschoen_eu: "",
  hallux: "", // "ja" | "nee"
  plt_order: "",
  scan_notities: "",
  plekken: [], // [{tempId, locatie, voet, zijde, pijncijfer, notitie}]
  voorgesteld_profiel: "",
};

export function AanmetingWizard({ uitvoering, werknemer, aanmeetdagId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const storageKey = `motera:aanmeting:${aanmeetdagId}:${werknemer.werknemer_id}`;
  const [state, setState, clearState] = useLocalStorageState(storageKey, EMPTY_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showNoShow, setShowNoShow] = useState(false);
  const [showVerzet, setShowVerzet] = useState(false);

  const step = state.step;
  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  function gotoStep(n) {
    if (n < 1 || n > TOTAL_STEPS) return;
    set({ step: n });
  }

  // ---- Quick-actions ----
  const noShowMut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/uitvoeringen/${uitvoering.uitvoering_id}/no-show`, body),
    onSuccess: () => { toast.success("No-show geregistreerd"); clearState(); navigate(`/aanmetingen/dag/${aanmeetdagId}`); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const verzetMut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/uitvoeringen/${uitvoering.uitvoering_id}/verzet`, body),
    onSuccess: () => { toast.success("Aanmeting verzet"); clearState(); navigate(`/aanmetingen/dag/${aanmeetdagId}`); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  // ---- Step 2 form ----
  const idForm = useForm({
    resolver: zodResolver(identiteitSchema),
    defaultValues: {
      voorletters: werknemer.voorletters || "",
      achternaam: werknemer.achternaam || "",
      geboortedatum: werknemer.geboortedatum || "",
      email: werknemer.email || "",
      telefoon: werknemer.telefoon || "",
      straat: werknemer.straat || "",
      huisnummer: werknemer.huisnummer || "",
      postcode: werknemer.postcode || "",
      plaats: werknemer.plaats || "",
      ...state.identiteit,
    },
  });

  useEffect(() => {
    const sub = idForm.watch((vals) => {
      set({ identiteit: vals, identiteitDirty: true });
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line
  }, []);

  // ---- Final submit chain ----
  async function voltooienSubmit() {
    setSubmitError(null);
    setSubmitting(true);
    const api = adminApiInstance();
    const uid = uitvoering.uitvoering_id;
    try {
      // 1. Identiteit-update (PATCH werknemer) - alleen relevante velden
      if (state.identiteitDirty && state.identiteit) {
        const i = state.identiteit;
        const updatePayload = {
          email: i.email || null,
          telefoon: i.telefoon || null,
          straat: i.straat || null,
          huisnummer: i.huisnummer || null,
          postcode: i.postcode || null,
          plaats: i.plaats || null,
        };
        await api.patch(`/api/admin/werknemers/${werknemer.werknemer_id}`, updatePayload);
      }
      // 2. Adres-controle
      await api.patch(`/api/admin/uitvoeringen/${uid}/adres-controle`, { gecontroleerd: true });
      // 3. Scan (combineert maatschoen + PLT + hallux + scan-notities)
      // Safe-placeholders die backend ScanVoetData-validatie (ge=50) passeren.
      // V1.5: PLT-app overschrijft deze met werkelijke metrics via webhook.
      const SAFE_VOET_PLACEHOLDER = { lengte_mm: 250, breedte_mm: 100, boog_hoogte_mm: 20 };
      const scanRef = JSON.stringify({
        plt_order: state.plt_order || "",
        hallux: state.hallux,
        scan_notities: state.scan_notities || "",
        placeholder: true, // markeer rij in rapportage als pre-PLT
      }).slice(0, 200);
      await api.patch(`/api/admin/uitvoeringen/${uid}/scan`, {
        voet_links:  { ...SAFE_VOET_PLACEHOLDER },
        voet_rechts: { ...SAFE_VOET_PLACEHOLDER },
        maatschoen_eu: parseFloat(state.maatschoen_eu),
        scan_data_referentie: scanRef,
      });
      // 4. Pijnplekken (1 POST per plek)
      for (const p of state.plekken) {
        await api.post(`/api/admin/uitvoeringen/${uid}/pijnplek`, {
          locatie: p.locatie, zijde: p.zijde, pijncijfer: p.pijncijfer,
        });
      }
      // 5. Profiel
      await api.patch(`/api/admin/uitvoeringen/${uid}/profiel`, {
        voorgesteld_profiel: state.voorgesteld_profiel,
      });
      // 6. Voltooien
      const voltRes = await api.patch(`/api/admin/uitvoeringen/${uid}/voltooien`, {});
      const v = voltRes.data;
      toast.success(
        v.bestelling_id
          ? "Aanmeting voltooid + bestelling aangemaakt"
          : "Aanmeting voltooid",
      );
      // Bewaar laatste 5 profielen voor suggesties (pilot 1 lokaal)
      try {
        const prev = JSON.parse(window.localStorage.getItem("motera:profiel-suggesties") || "[]");
        const next = [state.voorgesteld_profiel, ...prev.filter((x) => x !== state.voorgesteld_profiel)].slice(0, 5);
        window.localStorage.setItem("motera:profiel-suggesties", JSON.stringify(next));
      } catch { /* ignore */ }
      clearState();
      qc.invalidateQueries({ queryKey: ["uitvoeringen-dag", aanmeetdagId] });
      navigate(`/aanmetingen/dag/${aanmeetdagId}`);
    } catch (e) {
      setSubmitError(e);
    } finally {
      setSubmitting(false);
    }
  }

  const naam = [werknemer.voorletters, werknemer.achternaam].filter(Boolean).join(" ");

  return (
    <div data-testid="aanmeting-wizard">
      <ProgressRail step={step} total={TOTAL_STEPS} onJump={gotoStep} />
      <ErrorBanner error={submitError} />

      {step === 1 && (
        <Step1 werknemer={werknemer} naam={naam} onNoShow={() => setShowNoShow(true)} onVerzet={() => setShowVerzet(true)} onNext={() => gotoStep(2)} />
      )}
      {step === 2 && <Step2 form={idForm} onPrev={() => gotoStep(1)} onNext={() => idForm.handleSubmit(() => gotoStep(3))()} />}
      {step === 3 && <Step3 value={state.maatschoen_eu} onChange={(v) => set({ maatschoen_eu: v })} onPrev={() => gotoStep(2)} onNext={() => gotoStep(4)} />}
      {step === 4 && <Step4 value={state.hallux} onChange={(v) => set({ hallux: v })} onPrev={() => gotoStep(3)} onNext={() => gotoStep(5)} />}
      {step === 5 && <Step5 plt={state.plt_order} notities={state.scan_notities} onChangePlt={(v) => set({ plt_order: v })} onChangeNotities={(v) => set({ scan_notities: v })} onPrev={() => gotoStep(4)} onNext={() => gotoStep(6)} />}
      {step === 6 && (
        <div data-testid="step-6">
          <h2 className="mb-4 text-lg font-semibold">Stap 6 — Pijnlijke plekken</h2>
          <VoetDiagram plekken={state.plekken} onAdd={(p) => set({ plekken: [...state.plekken, p] })}
            onRemove={(tempId) => set({ plekken: state.plekken.filter((x) => x.tempId !== tempId) })} />
          <Footer step={6} onPrev={() => gotoStep(5)} onNext={() => gotoStep(7)} canNext={true} />
        </div>
      )}
      {step === 7 && (
        <Step7
          state={state} setState={setState} naam={naam}
          submitting={submitting} onPrev={() => gotoStep(6)} onSubmit={voltooienSubmit}
          onJump={gotoStep}
        />
      )}

      {showNoShow ? (
        <NoShowModal werknemerNaam={naam} onClose={() => setShowNoShow(false)}
          onSubmit={(body) => noShowMut.mutate(body)} loading={noShowMut.isPending} />
      ) : null}
      {showVerzet ? (
        <VerzetModal werknemerNaam={naam} currentAanmeetdagId={aanmeetdagId}
          onClose={() => setShowVerzet(false)}
          onSubmit={(body) => verzetMut.mutate(body)} loading={verzetMut.isPending} />
      ) : null}
    </div>
  );
}

function ProgressRail({ step, total, onJump }) {
  return (
    <div className="sticky top-0 z-10 -mx-4 mb-4 flex items-center gap-1 overflow-x-auto bg-slate-50 px-4 py-2 sm:mx-0" data-testid="wizard-progress">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <button key={n} type="button" onClick={() => onJump(n)} data-testid={`step-indicator-${n}`}
          className={`flex h-9 min-w-[40px] items-center justify-center rounded-md px-2 text-xs font-medium transition-colors
            ${n === step ? "bg-blue-600 text-white" : n < step ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
          {n}
        </button>
      ))}
      <span className="ml-auto text-xs text-slate-600" data-testid="wizard-progress-text">Stap {step} van {total}</span>
    </div>
  );
}

function Footer({ step, onPrev, onNext, canNext, nextLabel, submitting }) {
  return (
    <div className="mt-6 flex justify-between gap-3">
      <button type="button" onClick={onPrev} disabled={step === 1}
        data-testid="btn-prev"
        className="rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        Vorige
      </button>
      <button type="button" onClick={onNext} disabled={!canNext || submitting}
        data-testid="btn-next"
        className="min-h-[48px] rounded-md bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
        {submitting ? "Bezig…" : nextLabel || "Volgende"}
      </button>
    </div>
  );
}

function Step1({ werknemer, naam, onNoShow, onVerzet, onNext }) {
  return (
    <div data-testid="step-1">
      <h2 className="mb-4 text-lg font-semibold">Stap 1 — Werknemer bevestigen</h2>
      <dl className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-4 text-sm sm:grid-cols-3">
        <div><dt className="text-xs uppercase text-slate-500">Klantnummer</dt><dd className="font-mono">{werknemer.klantnummer}</dd></div>
        <div><dt className="text-xs uppercase text-slate-500">Naam</dt><dd>{naam}</dd></div>
        <div><dt className="text-xs uppercase text-slate-500">Werkgever</dt><dd>{werknemer.werkgever_naam || "—"}</dd></div>
      </dl>
      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" onClick={onNext} data-testid="btn-start-aanmeting"
          className="min-h-[48px] rounded-md bg-blue-600 px-6 py-3 text-base font-semibold text-white hover:bg-blue-700">
          Doorgaan
        </button>
        <button type="button" onClick={onNoShow} data-testid="btn-quick-no-show"
          className="min-h-[48px] rounded-md border border-rose-300 bg-white px-4 py-3 text-sm font-medium text-rose-700 hover:bg-rose-50">
          No-show
        </button>
        <button type="button" onClick={onVerzet} data-testid="btn-quick-verzet"
          className="min-h-[48px] rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
          Verzet
        </button>
      </div>
    </div>
  );
}

function Step2({ form, onPrev, onNext }) {
  const { register, formState: { errors }, watch } = form;
  function statusIcon(name) {
    const v = watch(name);
    if (!v) return <span aria-label="missend" className="text-amber-600" data-testid={`id-status-${name}`}>!</span>;
    return <span aria-label="ok" className="text-emerald-600" data-testid={`id-status-${name}`}>✓</span>;
  }
  function field(name, label, type = "text") {
    return (
      <label className="block text-sm">
        <span className="flex items-center justify-between font-medium text-slate-700"><span>{label}</span>{statusIcon(name)}</span>
        <input type={type} {...register(name)} aria-label={label} data-testid={`id-${name}`}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" />
        {errors[name] ? <p className="mt-1 text-xs text-rose-600" data-testid={`id-error-${name}`}>{errors[name].message}</p> : null}
      </label>
    );
  }
  return (
    <div data-testid="step-2">
      <h2 className="mb-4 text-lg font-semibold">Stap 2 — Identiteit-check</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-3">
          {field("voorletters", "Voorletters")}
          {field("achternaam", "Achternaam")}
          {field("geboortedatum", "Geboortedatum", "date")}
          {field("email", "E-mail", "email")}
          {field("telefoon", "Telefoon", "tel")}
        </div>
        <div className="space-y-3">
          {field("straat", "Straat")}
          {field("huisnummer", "Huisnummer")}
          {field("postcode", "Postcode")}
          {field("plaats", "Plaats")}
        </div>
      </div>
      <Footer step={2} onPrev={onPrev} onNext={onNext} canNext={true} />
    </div>
  );
}

function Step3({ value, onChange, onPrev, onNext }) {
  const num = parseFloat(value);
  const valid = !isNaN(num) && num >= 30 && num <= 55;
  return (
    <div data-testid="step-3">
      <h2 className="mb-4 text-lg font-semibold">Stap 3 — EU-schoenmaat</h2>
      <label className="block text-sm" htmlFor="maatschoen-eu">
        <span className="font-medium text-slate-700">Maatschoen EU (30 – 55, halve maten toegestaan)</span>
        <input id="maatschoen-eu" type="number" min={30} max={55} step={0.5}
          value={value} onChange={(e) => onChange(e.target.value)}
          aria-label="EU-schoenmaat" data-testid="field-maatschoen-eu"
          className="mt-2 h-14 w-40 rounded-md border border-slate-300 px-3 text-2xl tabular-nums focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" />
      </label>
      {!valid && value ? <p className="mt-2 text-xs text-rose-600" data-testid="maatschoen-error">Waarde moet tussen 30 en 55 zijn.</p> : null}
      <Footer step={3} onPrev={onPrev} onNext={onNext} canNext={valid} />
    </div>
  );
}

function Step4({ value, onChange, onPrev, onNext }) {
  return (
    <div data-testid="step-4">
      <h2 className="mb-4 text-lg font-semibold">Stap 4 — Hallux-test</h2>
      {/* PRE-LAUNCH content — Steven levert na Peter-overleg een uitleg-video voor V1.5 */}
      <div data-testid="hallux-uitleg"
        className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-800">
        <div className="flex items-start gap-3">
          <svg data-testid="hallux-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 96"
            width="48" height="72" aria-hidden="true" className="shrink-0">
            {/* Minimal voet-silhouet van boven, grote-teen-zone in oranje highlight */}
            <ellipse cx="32" cy="60" rx="18" ry="30" fill="#e2e8f0" stroke="#475569" strokeWidth="1.5" />
            {/* Grote teen — oranje highlight */}
            <ellipse cx="22" cy="22" rx="6" ry="9" fill="#fb923c" stroke="#c2410c" strokeWidth="1.5" />
            {/* Overige tenen */}
            <ellipse cx="32" cy="20" rx="4" ry="7" fill="#cbd5e1" stroke="#64748b" strokeWidth="1" />
            <ellipse cx="40" cy="22" rx="3.5" ry="6" fill="#cbd5e1" stroke="#64748b" strokeWidth="1" />
            <ellipse cx="46" cy="26" rx="3" ry="5" fill="#cbd5e1" stroke="#64748b" strokeWidth="1" />
            <ellipse cx="51" cy="32" rx="2.5" ry="4" fill="#cbd5e1" stroke="#64748b" strokeWidth="1" />
          </svg>
          <p className="leading-relaxed">
            De hallux-test bepaalt of er een ondersteunend element onder de grote teen in de zool moet.
            Vraag de werknemer om op de tablet te tikken op het juiste antwoord: kan de grote teen normaal
            afwikkelen (Ja) of is er een rigiditeit/pijn-respons (Nee)?
          </p>
        </div>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-slate-700">Hallux beweegbaar?</legend>
        {["ja", "nee"].map((v) => (
          <label key={v} className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-md border px-4 py-3 text-base
            ${value === v ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white hover:bg-slate-50"}`}>
            <input type="radio" name="hallux" value={v} checked={value === v}
              onChange={() => onChange(v)} aria-label={`Hallux ${v}`} data-testid={`hallux-${v}`}
              className="h-5 w-5" />
            <span className="font-medium capitalize">{v}</span>
          </label>
        ))}
      </fieldset>
      <Footer step={4} onPrev={onPrev} onNext={onNext} canNext={value === "ja" || value === "nee"} />
    </div>
  );
}

function Step5({ plt, notities, onChangePlt, onChangeNotities, onPrev, onNext }) {
  return (
    <div data-testid="step-5">
      <h2 className="mb-4 text-lg font-semibold">Stap 5 — Scan-referentie</h2>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">PLT order-ID</span>
        <input type="text" value={plt} onChange={(e) => onChangePlt(e.target.value)} maxLength={100}
          aria-label="PLT order-ID" data-testid="field-plt-order"
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-3 text-base focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <p className="mt-1 text-xs text-slate-500">Voer hier de PLT-scan-referentie in (gegenereerd in PLT-app).</p>
      </label>
      <label className="mt-4 block text-sm">
        <span className="font-medium text-slate-700">Notities (optioneel)</span>
        <textarea value={notities} onChange={(e) => onChangeNotities(e.target.value)} maxLength={500} rows={3}
          aria-label="Scan-notities" data-testid="field-scan-notities"
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <Footer step={5} onPrev={onPrev} onNext={onNext} canNext={plt.trim().length > 0} />
    </div>
  );
}

function Step7({ state, setState, naam, submitting, onPrev, onSubmit, onJump }) {
  const [suggesties, setSuggesties] = useState([]);
  useEffect(() => {
    try {
      setSuggesties(JSON.parse(window.localStorage.getItem("motera:profiel-suggesties") || "[]"));
    } catch { setSuggesties([]); }
  }, []);
  const valid = state.voorgesteld_profiel?.trim().length >= 2;
  return (
    <div data-testid="step-7">
      <h2 className="mb-4 text-lg font-semibold">Stap 7 — Voorgesteld profiel + Samenvatting</h2>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Voorgesteld profiel</span>
        <input list="profiel-suggesties" type="text" value={state.voorgesteld_profiel}
          onChange={(e) => setState((s) => ({ ...s, voorgesteld_profiel: e.target.value }))}
          maxLength={50} aria-label="Voorgesteld profiel" data-testid="field-profiel"
          placeholder="bv. mid + grote teen + gecertificeerd"
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-3 text-base focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <datalist id="profiel-suggesties" data-testid="profiel-suggesties">
          {suggesties.map((s, i) => <option key={i} value={s} />)}
        </datalist>
      </label>

      <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">Samenvatting</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard step={2} label="Identiteit" testid="summary-id" onEdit={() => onJump(2)}>
          {naam || "—"} · {state.identiteit?.email || "—"} · {state.identiteit?.straat || "—"} {state.identiteit?.huisnummer || ""}, {state.identiteit?.postcode || ""} {state.identiteit?.plaats || ""}
        </SummaryCard>
        <SummaryCard step={3} label="EU-schoenmaat" testid="summary-maat" onEdit={() => onJump(3)}>
          {state.maatschoen_eu || "—"}
        </SummaryCard>
        <SummaryCard step={4} label="Hallux" testid="summary-hallux" onEdit={() => onJump(4)}>
          {state.hallux || "—"}
        </SummaryCard>
        <SummaryCard step={5} label="Scan-referentie" testid="summary-scan" onEdit={() => onJump(5)}>
          PLT: {state.plt_order || "—"}
        </SummaryCard>
        <SummaryCard step={6} label={`Pijnplekken (${state.plekken.length})`} testid="summary-plekken" onEdit={() => onJump(6)}>
          {state.plekken.length === 0 ? "Geen plekken" : state.plekken.map((p) => `${noemNetjes(p.locatie, PIJN_LOCATIE_LABELS)}/${p.voet}${p.pijncijfer >= 7 ? "⚑" : ""}`).join(" · ")}
        </SummaryCard>
      </div>

      <div className="mt-6 flex justify-between gap-3">
        <button type="button" onClick={onPrev} data-testid="btn-prev"
          className="rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700">
          Vorige
        </button>
        <button type="button" onClick={onSubmit} disabled={!valid || submitting} data-testid="btn-voltooien"
          className="min-h-[48px] rounded-md bg-emerald-600 px-6 py-3 text-base font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
          {submitting ? "Verwerken…" : "Voltooien"}
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ step, label, testid, onEdit, children }) {
  return (
    <button type="button" onClick={onEdit} data-testid={testid}
      className="rounded-md border border-slate-200 bg-white p-3 text-left transition-colors hover:border-blue-400">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase text-slate-500">{label}</span>
        <span className="text-xs text-blue-600">Bewerk →</span>
      </div>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </button>
  );
}
